import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import paths from '@/routes/paths';
import { ReactComponent as MainLogo } from '@/assets/icons/svg/common/main-logo.svg';
import { ReactComponent as InquiryArrow } from '@/assets/icons/svg/common/inquiry-arrow.svg';
import { ReactComponent as HambugerIcon } from '@/assets/icons/svg/common/hambuger.svg';
import MobileMenu from './mobile-menu';
import './header.scss';

import { menuList } from '@/data/navigation';
export type { menuListType } from '@/data/navigation';

export default function Header({
	isLandingPage = false
}: {
	isLandingPage?: boolean;
}) {
	const [scrolled, setScrolled] = useState(false);
	const [openMenu, setOpenMenu] = useState<number | null>(null);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const root = useRef<HTMLElement>(null);
	const hamburger = useRef<HTMLButtonElement>(null);
	const location = useLocation();
	const isScrolled = !isLandingPage || scrolled;

	useEffect(() => {
		if (!isLandingPage) return;
		const onScroll = () => setScrolled(window.scrollY > 0);
		window.addEventListener('scroll', onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener('scroll', onScroll);
	}, [isLandingPage]);
	useEffect(() => {
		setOpenMenu(null);
		setMobileMenuOpen(false);
	}, [location]);
	useEffect(() => {
		const outside = (event: PointerEvent) => {
			if (!root.current?.contains(event.target as Node))
				setOpenMenu(null);
		};
		const desktop = window.matchMedia('(min-width: 816px)');
		const resize = () => {
			setOpenMenu(null);
			if (desktop.matches) setMobileMenuOpen(false);
		};
		document.addEventListener('pointerdown', outside);
		desktop.addEventListener('change', resize);
		return () => {
			document.removeEventListener('pointerdown', outside);
			desktop.removeEventListener('change', resize);
		};
	}, []);

	return (
		<header
			ref={root}
			className={classNames('header-root', {
				scrolled: isScrolled,
				'menu-expanded': openMenu !== null,
				'mobile-menu-open': mobileMenuOpen,
				'content-header': location.pathname.startsWith('/content')
			})}
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					const id = openMenu;
					setOpenMenu(null);
					root.current
						?.querySelector<HTMLButtonElement>(
							`#header-toggle-${id}`
						)
						?.focus();
				}
			}}
		>
			<div className="header-inner-wrapper">
				<Link
					to={paths.home}
					className="header-logo"
					data-analytics-cta="header_home"
					data-analytics-label="홈으로 이동"
					aria-label="미스터디벨로 홈으로 이동"
				>
					<MainLogo
						className={classNames('header-logo-icon', {
							scrolled: isScrolled
						})}
					/>
				</Link>
				<nav
					className={classNames('landing-menu-box', {
						scrolled: isScrolled
					})}
					aria-label="주 메뉴"
				>
					<ul className="menu-list">
						{menuList.map((menu) => {
							const active =
								location.pathname === menu.path ||
								location.pathname.startsWith(`${menu.path}/`) ||
								(menu.id === 3 &&
									location.pathname === paths.column);
							const open = openMenu === menu.id;
							return (
								<li
									key={menu.id}
									className={classNames('menu-item', {
										active,
										scrolled: isScrolled,
										'hover-effect': open
									})}
									onBlur={(event) => {
										if (
											!event.currentTarget.contains(
												event.relatedTarget as Node
											)
										)
											setOpenMenu((current) =>
												current === menu.id
													? null
													: current
											);
									}}
								>
									{menu.subMenu ? (
										<button
											type="button"
											id={`header-toggle-${menu.id}`}
											className="text-link menu-toggle"
											aria-expanded={open}
											aria-controls={`header-submenu-${menu.id}`}
											onClick={() =>
												setOpenMenu(
													open ? null : menu.id
												)
											}
										>
											{menu.label}
											<svg
												className="menu-chevron"
												aria-hidden="true"
												focusable="false"
												viewBox="0 0 24 24"
												fill="none"
											>
												<path
													d="m6 9 6 6 6-6"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</button>
									) : (
										<Link
											data-analytics-cta={`header_menu_${menu.id}`}
											data-analytics-label={menu.label}
											className="text-link"
											to={menu.path}
											aria-current={
												active ? 'page' : undefined
											}
										>
											{menu.label}
										</Link>
									)}
									<div
										className="menu-item-underline"
										aria-hidden="true"
									/>
									{menu.subMenu && (
										<div
											id={`header-submenu-${menu.id}`}
											className={classNames(
												'sub-menu-box',
												{
													open,
													'content-sub-menu':
														menu.id === 3
												}
											)}
											hidden={!open}
										>
											<ul className="sub-menu-list">
												{menu.subMenu.map((sub) => (
													<li
														key={sub.id}
														className={classNames(
															'sub-menu-item',
															{
																active:
																	location.pathname ===
																	sub.path
															}
														)}
													>
														<Link
															to={sub.path}
															aria-current={
																location.pathname ===
																sub.path
																	? 'page'
																	: undefined
															}
															data-analytics-cta={`header_submenu_${sub.id}`}
															data-analytics-label={
																sub.label
															}
														>
															{sub.label}
														</Link>
													</li>
												))}
											</ul>
										</div>
									)}
								</li>
							);
						})}
					</ul>
					<Link
						to={paths.inquiry}
						data-analytics-cta="header_inquiry"
						data-analytics-label="문의하기"
						className={classNames('inquiry-button', {
							scrolled: isScrolled
						})}
					>
						<p>문의하기</p>
						<InquiryArrow
							className={classNames('inquiry-arrow', {
								scrolled: isScrolled
							})}
						/>
					</Link>
					<button
						ref={hamburger}
						type="button"
						aria-label="메뉴 열기"
						aria-expanded={mobileMenuOpen}
						aria-controls="mobile-navigation"
						className="hambuger-menu-box"
						onClick={() => setMobileMenuOpen(true)}
					>
						<HambugerIcon className="hambuger-icon" />
					</button>
				</nav>
			</div>
			<MobileMenu
				mobileMenuOpen={mobileMenuOpen}
				setMobileMenuOpen={setMobileMenuOpen}
				menuList={menuList}
				returnFocus={() => hamburger.current?.focus()}
			/>
		</header>
	);
}
